import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Card, CardHeader, CardContent, CardTitle } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { ChevronDown, ChevronUp, Package } from "lucide-react";
import { formatDate } from "date-fns";
import { STATUS_COLORS, STATUS_OPTIONS } from "@shared/utils/constants";

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();

  // Use STATUS_COLORS from constants for consistent styling
  const statusClass = STATUS_COLORS[status] || "bg-gray-100 text-gray-600 border-gray-200";

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium border ${statusClass}`}
    >
      {t(STATUS_OPTIONS[status] || `status.${status}`)}
    </span>
  );
};

const TrackingEvent = ({ event, isLast }) => {
  const { t } = useTranslation();
  
  // Helper function to format user display
  const formatUser = (user) => {
    if (!user) return "System";
    
    // If user is a string (like "system" or user ID), return as is
    if (typeof user === "string") {
      return user === "system" ? "System" : user;
    }
    
    // If user is an object with user details
    if (typeof user === "object") {
      // Priority: brandName > fullName > email
      const displayName = user.brandName || user.fullName || user.email || "Unknown User";
      const role = user.role ? ` (${user.role})` : "";
      return `${displayName}${role}`;
    }
    
    return "System";
  };

  return (
    <div className="flex">
      <div className="flex flex-col items-center ltr:mr-4 rtl:ml-4">
        <div className="w-3 h-3 bg-blue-500 rounded-full" />
        {!isLast && <div className="w-0.5 h-full bg-gray-200" />}
      </div>
      <div
        className={`pb-8 ${
          isLast ? "" : "ltr:border-l-2 rtl:border-r-2  border-transparent"
        }`}
      >
        <div className="flex items-center gap-2 mb-2">
          <p className="text-xs text-gray-500">
            {formatDate(event.date, "dd/MM/yyyy hh:mm")}
          </p>
          <StatusBadge status={event.status} />
        </div>
        <p className="text-gray-600 text-sm mb-2">{event.note}</p>
        
        {/* Enhanced user display */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{t("packages.updatedBy")}:</span>
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary text-secondary">
            {formatUser(event.user)}
          </span>
          {/* Show phone number if available */}
          {typeof event.user === "object" && event.user?.phone && (
            <span className="text-xs text-gray-400">• {event.user.phone}</span>
          )}
        </div>
      </div>
    </div>
  );
};

const TrackingPackages = ({ statusHistory }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const processedEvents = statusHistory;
  const visibleEvents = expanded
    ? processedEvents
    : processedEvents.slice(0, 5);

  return (
    <Card className="w-full md:max-w-sm mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-6 h-6" />
          {t("packages.packageTracking")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className=" pr-4">
          {processedEvents.length > 0 && (
            <div className="space-y-6">
              {visibleEvents.map((event, index) => (
                <TrackingEvent
                  key={`${event.time}-${index}`}
                  event={event}
                  isLast={index === visibleEvents.length - 1}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {processedEvents.length > 5 && (
          <Button
            variant="outline"
            className="w-full mt-4"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4 mr-2" />
                {t("packages.showLess")}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-2" />
                {t("packages.showMore")} ({processedEvents.length - 5} {t("packages.moreEvents")})
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default TrackingPackages;
